<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-inbox-in"></i>
        {{ $t('deliveryProof.queue_title') }}
        <span class="countTotal" v-if="unreviewedCount">({{ unreviewedCount }})</span>
      </h4>
      <div class="header-actions">
        <!-- An empty queue has two very different causes — nothing to review,
             or no sender enabled at all — and only that screen tells them
             apart. -->
        <button class="button" @click="$router.push({ name: 'delivery.proof.senders' })">
          {{ $t('deliveryProof.enabled_senders_button') }}
        </button>
        <button class="button is-info" @click="$router.push({ name: 'delivery.proof.report' })">
          {{ $t('deliveryProof.go_to_report_button') }}
        </button>
      </div>
    </div>

    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-4">
          <label class="label">{{ $t('deliveryProof.branch') }}:</label>
          <BranchSelect v-model="branchId" :placeholder="$t('deliveryProof.all')" @input="fetchQueue(1)" />
        </div>
        <div class="column is-4" style="display:flex;align-items:flex-end">
          <button class="button is-primary" @click="fetchQueue(1)">{{ $t('deliveryProof.reload_button') }}</button>
        </div>
      </div>
    </div>

    <!-- Opens on ALL, and that is a control rather than a preference: if the
         default hid rejected proofs, rejecting would be the cheapest way to
         empty the queue. -->
    <div class="tabs">
      <ul>
        <li v-for="tab in tabs" :key="tab.key" :class="{ 'is-active': filter === tab.key }">
          <a @click="filter = tab.key">{{ tab.label }}</a>
        </li>
      </ul>
    </div>

    <p v-if="lastReportError" class="report-error">{{ lastReportError }}</p>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('deliveryProof.bill_number') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.branch') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.submitted_at') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.waiting_age') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.bill_status') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.review_result') }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr v-for="row in visibleRows" :key="row.id" class="clickable"
                 @click.native="openDetail(row)">
            <vs-td><strong>{{ row.bill_number || row.shipment_id }}</strong></vs-td>
            <vs-td>{{ row.branch_name || ('#' + row.end_branch_id) }}</vs-td>
            <vs-td>{{ shortDateTime(row.submitted_at) }}</vs-td>
            <!-- The proof nobody opened is the worst outcome in the report, so
                 ageing is shown rather than left for someone to subtract. -->
            <vs-td :class="ageClass(row)">{{ ageLabel(row) }}</vs-td>
            <vs-td>{{ closureLabel(row.bill_closure_state) }}</vs-td>
            <vs-td>{{ reviewLabel(row.status) }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <p v-if="!loading && visibleRows.length === 0" class="empty-line">
        {{ $t('deliveryProof.empty_queue') }}
      </p>

      <div v-if="lastPage > 1" class="pager">
        <button class="button" :disabled="page <= 1" @click="fetchQueue(page - 1)">{{ $t('deliveryProof.previous_page') }}</button>
        <span>{{ page }} / {{ lastPage }}</span>
        <button class="button" :disabled="page >= lastPage" @click="fetchQueue(page + 1)">{{ $t('deliveryProof.next_page') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import DeliveryProofApi from '@/Mixins/DeliveryProof/DeliveryProofApi';
import BranchSelect from '@/components/BranchSelect.vue';

/**
 * Head office's review queue, across every branch.
 *
 * The reviewer is deliberately not the branch the evidence is about: a branch
 * judging its own proofs could reject its way to a clean queue, and the feature
 * would end up measuring how quickly a branch rejects rather than how often it
 * forgets to key a bill.
 */
export default {
    name: 'DeliveryProofQueue',
    components: { BranchSelect },
    mixins: [DeliveryProofApi],

    data() {
        return {
            rows: [],
            branchId: '',
            filter: 'all',
            unreviewedCount: 0,
            page: 1,
            lastPage: 1,
            loading: false,
            tabs: [
                { key: 'all', label: this.$t('deliveryProof.all') },
                { key: 'pending', label: this.$t('deliveryProof.status_pending') },
                { key: 'approved', label: this.$t('deliveryProof.status_approved') },
                { key: 'rejected', label: this.$t('deliveryProof.status_rejected') },
            ],
        };
    },

    computed: {
        /** A VIEW over what came back — never a request for less. */
        visibleRows() {
            return this.filter === 'all'
                ? this.rows
                : this.rows.filter((r) => r.status === this.filter);
        },
    },

    mounted() {
        this.fetchQueue(1);
    },

    methods: {
        fetchQueue(page) {
            this.loading = true;
            this.lastReportError = null;

            const params = { page, limit: 20 };
            if (this.branchId) params.branch_id = this.branchId;

            this.deliveryProofQueue(params)
                .then(({ data }) => {
                    const payload = data.data;
                    this.rows = payload.items || [];
                    this.page = payload.current_page || 1;
                    this.lastPage = payload.last_page || 1;
                    this.unreviewedCount = payload.unreviewed_count || 0;
                })
                .catch((error) => {
                    this.rows = [];
                    this.handleReportError(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        openDetail(row) {
            this.$router.push({ name: 'delivery.proof.detail', params: { id: row.id } });
        },

        daysBetween(from, to) {
            return Math.max(0, Math.floor((to - new Date(from).getTime()) / 86400000));
        },

        /**
         * How long this has been waiting for someone to look at it. ONE meaning.
         *
         * It carried two before — waiting time on pending rows, time-taken on
         * reviewed ones — and needed a slash in its header to say which. A
         * column that has to be explained is a column that gets misread, and how
         * long a finished review took is a measure of us, not a piece of work
         * outstanding: the report already has it as ຊ້າສະເລ່ຍ.
         *
         * Reviewed rows are blank, because nothing is waiting on them.
         */
        ageLabel(row) {
            if (row.status !== 'pending') return '—';

            const days = this.daysBetween(row.submitted_at, Date.now());
            return days === 0 ? this.$t('deliveryProof.today') : this.$t('deliveryProof.days_count', { n: days });
        },

        /** Only what is still waiting can be late. */
        ageClass(row) {
            if (row.status !== 'pending') return '';
            const days = this.daysBetween(row.submitted_at, Date.now());
            if (days >= 3) return 'age-old';
            if (days >= 1) return 'age-warm';
            return '';
        },

        closureLabel(state) {
            return {
                open: this.$t('deliveryProof.closure_open'),
                keyed_by_person: this.$t('deliveryProof.closure_keyed_by_person'),
                auto_signed: this.$t('deliveryProof.closure_auto_signed'),
            }[state] || '—';
        },

        reviewLabel(status) {
            return {
                pending: this.$t('deliveryProof.status_pending'),
                approved: this.$t('deliveryProof.status_approved'),
                rejected: this.$t('deliveryProof.status_rejected'),
            }[status] || status;
        },

        shortDateTime(value) {
            if (!value) return '—';
            const d = new Date(value);
            return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.report-error {
    margin-bottom: 12px;
    padding: 10px;
    background: #fdecea;
    color: #b71c1c;
    border-radius: 4px;
}

.clickable {
    cursor: pointer;
}

.header-actions {
    display: flex;
    gap: 8px;
}

.age-warm {
    color: #c77700;
    font-weight: 600;
}

.age-old {
    color: #b71c1c;
    font-weight: 700;
}

.empty-line {
    padding: 24px 0;
    text-align: center;
    color: #666;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
}
</style>
